import type {
  ACLAction as ACLActionValue,
  ACLDefaultRemoteCIDR,
  ACLDirection as ACLDirectionValue,
  ACLL3RuleState as ACLL3RuleStateValue,
  ACLProtocol as ACLProtocolValue,
  ACLProtocolName as ACLProtocolNameValue,
  ACLResourceStatus as ACLResourceStatusValue,
  ACLResourceType as ACLResourceTypeValue,
} from '@/consts/acl'
import {
  ACL_DEFAULT_REMOTE_CIDRS,
  ACL_L3_RULE_LIMIT,
  ACL_L4_RULE_LIMIT,
  ACLAction as ACLActionConst,
  ACLDirection as ACLDirectionConst,
  ACLL3RuleState as ACLL3RuleStateConst,
  ACLProtocol as ACLProtocolConst,
  ACLProtocolName as ACLProtocolNameConst,
  ACLResourceStatus as ACLResourceStatusConst,
  ACLResourceType as ACLResourceTypeConst,
} from '@/consts/acl'

export const ACLAction = ACLActionConst
export type ACLAction = ACLActionValue

export const ACLDirection = ACLDirectionConst
export type ACLDirection = ACLDirectionValue

export const ACLProtocol = ACLProtocolConst
export type ACLProtocol = ACLProtocolValue

export const ACLProtocolName = ACLProtocolNameConst
export type ACLProtocolName = ACLProtocolNameValue

export const ACLResourceStatus = ACLResourceStatusConst
export type ACLResourceStatus = ACLResourceStatusValue

export const ACLResourceType = ACLResourceTypeConst
export type ACLResourceType = ACLResourceTypeValue

export const ACLL3RuleState = ACLL3RuleStateConst
export type ACLL3RuleState = ACLL3RuleStateValue

export { ACL_DEFAULT_REMOTE_CIDRS, ACL_L3_RULE_LIMIT, ACL_L4_RULE_LIMIT }
export type { ACLDefaultRemoteCIDR }

export const aclDirectionLabelKey: Record<ACLDirectionValue, string> = {
  [ACLDirection.Inbound]: 'aclRules.directionInbound',
  [ACLDirection.Outbound]: 'aclRules.directionOutbound',
}

export const aclActionMeta: Record<ACLActionValue, { key: string; cls: string }> = {
  [ACLAction.Allow]: { key: 'aclRules.actionAllow', cls: 'text-active' },
  [ACLAction.Deny]: { key: 'aclRules.actionDeny', cls: 'text-error' },
}

export const aclL3RuleStateMeta: Record<ACLL3RuleStateValue, { key: string; cls: string }> = {
  [ACLL3RuleState.Invalid]: { key: 'aclRules.stateInvalid', cls: 'text-error' },
  [ACLL3RuleState.Pending]: { key: 'aclRules.statePending', cls: 'text-highlight' },
  [ACLL3RuleState.Applied]: { key: 'aclRules.stateApplied', cls: 'text-active' },
}

export function aclProtocolLabel(
  protocol: number | string,
  portStart?: number,
  portEnd?: number,
): string {
  const isAnyPort = (portStart === 0 || portStart === 1) && portEnd === 65535

  let protocolNum: number | undefined
  if (typeof protocol === 'string') {
    const upper = protocol.toUpperCase()
    if (upper === 'ANY' || upper === '') protocolNum = ACLProtocol.ANY
    else if (upper === 'ICMP') protocolNum = ACLProtocol.ICMP
    else if (upper === 'TCP') protocolNum = ACLProtocol.TCP
    else if (upper === 'UDP') protocolNum = ACLProtocol.UDP
  } else {
    protocolNum = protocol
  }

  switch (protocolNum) {
    case ACLProtocol.ANY:
      return 'ANY'
    case ACLProtocol.ICMP:
      return 'Any ICMPv4'
    case ACLProtocol.TCP:
      return isAnyPort ? 'Any TCP' : 'Custom TCP'
    case ACLProtocol.UDP:
      return isAnyPort ? 'Any UDP' : 'Custom UDP'
    default:
      return '-'
  }
}

export function formatPortRange(start: number, end: number): string {
  if (!start && !end) return 'All port'
  if (start === end) return String(start)
  return `${start}-${end}`
}

// CIDR `0.0.0.0/0` (and IPv6 equivalent) is treated as the implicit default rule.
export function isDefaultCIDR(cidr: string): boolean {
  return ACL_DEFAULT_REMOTE_CIDRS.includes(cidr as (typeof ACL_DEFAULT_REMOTE_CIDRS)[number])
}
